/**
 * Recovery Key System - BIP39 24-word mnemonic for workspace seed recovery
 *
 * Provides cryptographically secure recovery mechanism independent of device keys.
 * If all devices are lost, user can enter recovery phrase to restore access.
 */

import * as bip39 from 'bip39';
import { unwrapSeed } from './keyManagement.js';

const DISALLOWED_WORDS = new Set([
  'dead', 'death', 'die', 'doom', 'evil', 'fatal', 'kill',
  'murder', 'plague', 'poison', 'suicide', 'terror',
]);

// The X25519 base point, u = 9 (RFC 7748). X25519(k, 9) is the public key for k,
// so WebCrypto derives it without the private key ever being extractable.
const X25519_BASE_POINT = new Uint8Array(32);
X25519_BASE_POINT[0] = 9;

async function publicKeyBytesFor(privateKey: CryptoKey): Promise<Uint8Array> {
  const basePoint = await crypto.subtle.importKey(
    'raw',
    X25519_BASE_POINT as unknown as BufferSource,
    { name: 'X25519' },
    false,
    []
  );
  return new Uint8Array(await crypto.subtle.deriveBits({ name: 'X25519', public: basePoint }, privateKey, 256));
}

/**
 * Generate a 24-word BIP39 mnemonic recovery phrase.
 * Retries up to 10 times to avoid alarming words; falls back to an unfiltered phrase.
 */
export async function generateRecoveryPhrase(): Promise<string> {
  for (let attempt = 0; attempt < 10; attempt++) {
    const mnemonic = bip39.generateMnemonic(256);
    if (bip39.validateMnemonic(mnemonic) && !mnemonic.split(' ').some(w => DISALLOWED_WORDS.has(w))) {
      return mnemonic;
    }
  }
  return bip39.generateMnemonic(256);
}

/**
 * Validate a recovery phrase (check BIP39 format and checksum)
 */
export function validateRecoveryPhrase(phrase: string): boolean {
  return bip39.validateMnemonic(phrase.trim());
}

/**
 * Derive an asymmetric X25519 keypair from the 24-word recovery phrase.
 * The public key can be safely published on the server to allow any device
 * to wrap current and future workspace seeds for recovery.
 */
export async function deriveRecoveryKeyPair(phrase: string): Promise<{
  publicKey: CryptoKey;
  privateKey: CryptoKey;
  publicKeyBase64: string;
}> {
  if (!validateRecoveryPhrase(phrase)) {
    throw new Error('Invalid recovery phrase - checksum failed');
  }

  const seed = await bip39.mnemonicToSeed(phrase.trim());

  const baseKey = await crypto.subtle.importKey(
    'raw',
    new Uint8Array(seed) as unknown as BufferSource,
    { name: 'HKDF' },
    false,
    ['deriveBits']
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode('yada-recovery-salt-v1'),
      info: new TextEncoder().encode('yada-recovery-x25519-v1'),
    },
    baseKey,
    256
  );

  const rawPriv = new Uint8Array(derivedBits);
  rawPriv[0] &= 248;
  rawPriv[31] &= 127;
  rawPriv[31] |= 64;

  // PKCS#8 DER header for X25519
  const pkcs8Header = new Uint8Array([
    0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x6e, 0x04, 0x22, 0x04, 0x20
  ]);
  const pkcs8 = new Uint8Array(pkcs8Header.length + rawPriv.length);
  pkcs8.set(pkcs8Header, 0);
  pkcs8.set(rawPriv, pkcs8Header.length);

  const privateKey = await crypto.subtle.importKey(
    'pkcs8',
    pkcs8 as unknown as BufferSource,
    { name: 'X25519' },
    false,
    ['deriveKey', 'deriveBits']
  );

  rawPriv.fill(0);
  pkcs8.fill(0);

  const rawPub = await publicKeyBytesFor(privateKey);
  const publicKey = await crypto.subtle.importKey(
    'raw',
    rawPub as unknown as BufferSource,
    { name: 'X25519' },
    true,
    []
  );

  let binary = '';
  for (let i = 0; i < rawPub.length; i++) {
    binary += String.fromCharCode(rawPub[i]);
  }
  const publicKeyBase64 = btoa(binary);

  return {
    publicKey,
    privateKey,
    publicKeyBase64,
  };
}

/**
 * Import an X25519 recovery public key from base64 string.
 */
export async function importRecoveryPublicKey(base64: string): Promise<CryptoKey> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return await crypto.subtle.importKey(
    'raw',
    bytes as unknown as BufferSource,
    { name: 'X25519' },
    true,
    []
  );
}

/**
 * Derive a 256-bit encryption key from recovery phrase (legacy symmetric AES-GCM)
 * Uses PBKDF2 with 100,000 iterations (BIP39 standard)
 */
export async function deriveKeyFromRecoveryPhrase(
  phrase: string,
  _salt: string = 'yada-recovery-v1'
): Promise<CryptoKey> {
  if (!validateRecoveryPhrase(phrase)) {
    throw new Error('Invalid recovery phrase - checksum failed');
  }

  const seed = await bip39.mnemonicToSeed(phrase.trim());
  const keyMaterial = seed.slice(0, 32);

  return await crypto.subtle.importKey(
    'raw',
    new Uint8Array(keyMaterial) as unknown as BufferSource,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
  );
}

/**
 * Wrap (encrypt) a workspace seed with legacy recovery key
 */
export async function wrapSeedWithRecovery(
  seed: ArrayBuffer,
  recoveryKey: CryptoKey
): Promise<ArrayBuffer> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedSeed = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    recoveryKey,
    seed
  );

  const wrapped = new Uint8Array(iv.length + encryptedSeed.byteLength);
  wrapped.set(iv, 0);
  wrapped.set(new Uint8Array(encryptedSeed), iv.length);

  return wrapped.buffer;
}

/**
 * Unwrap (decrypt) a workspace seed with legacy recovery key
 */
export async function unwrapSeedWithRecovery(
  wrappedSeed: ArrayBuffer,
  recoveryKey: CryptoKey
): Promise<ArrayBuffer> {
  const wrapped = new Uint8Array(wrappedSeed);
  const iv = wrapped.slice(0, 12);
  const encryptedSeed = wrapped.slice(12);

  try {
    return await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      recoveryKey,
      encryptedSeed
    );
  } catch (_err) {
    throw new Error('Failed to decrypt seed with recovery key - phrase may be incorrect');
  }
}

/**
 * Decrypt a recovery-wrapped seed using either the modern asymmetric X25519
 * recovery keypair or falling back to legacy symmetric AES-GCM recovery.
 */
export async function unwrapRecoverySeed(
  wrappedSeed: ArrayBuffer,
  phrase: string,
  wrapAlgorithm?: string
): Promise<ArrayBuffer> {
  if (wrapAlgorithm === 'X25519-AES-GCM' || wrapAlgorithm === 'RSA-OAEP-256' || !wrapAlgorithm) {
    try {
      const { privateKey } = await deriveRecoveryKeyPair(phrase);
      return await unwrapSeed(wrappedSeed, privateKey);
    } catch (err) {
      if (wrapAlgorithm === 'X25519-AES-GCM') throw err;
      // If algorithm wasn't specified and asymmetric failed, try symmetric fallback
    }
  }

  // Fallback to legacy symmetric AES-GCM
  const legacyKey = await deriveKeyFromRecoveryPhrase(phrase);
  return await unwrapSeedWithRecovery(wrappedSeed, legacyKey);
}

/**
 * Format recovery phrase for display (4 columns of 6 words each)
 */
export function formatRecoveryPhrase(phrase: string): Array<{ number: string; word: string }> {
  const words = phrase.trim().split(/\s+/);
  return words.map((word, i) => ({
    number: (i + 1).toString().padStart(2, '0'),
    word,
  }));
}

/**
 * Check if recovery phrase has been saved (stored in localStorage as flag)
 */
export function hasRecoveryPhraseSaved(): boolean {
  return localStorage.getItem('yada:recovery:saved') === 'true';
}

/**
 * Mark recovery phrase as saved by user
 */
export function markRecoveryPhraseSaved(): void {
  localStorage.setItem('yada:recovery:saved', 'true');
}

/**
 * Clear recovery phrase saved flag (for testing/reset)
 */
export function clearRecoveryPhraseSaved(): void {
  localStorage.removeItem('yada:recovery:saved');
  localStorage.removeItem('yada:recovery:publicKey');
}

